import React, { useState, useEffect } from 'react';
import { useDatabase } from '../../contexts/DatabaseContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import schema from './schema';
import validator from './validator';
import defaultData from './defaultData';
import { ZsCalculator } from '../../utils/electrical/bs7671Tables.js';

const { FiPlus, FiTrash2, FiSave, FiFileText } = FiIcons;

const EICRForm = ({ formInstanceId, onSave, onGeneratePDF }) => {
  const { db } = useDatabase();
  const [formData, setFormData] = useState(defaultData);
  const [errors, setErrors] = useState({});
  const [warnings, setWarnings] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (formInstanceId) loadFormData(formInstanceId);
  }, [formInstanceId]);

  const loadFormData = async (instanceId) => {
    try {
      const form = await db.forms.get(parseInt(instanceId));
      if (form && form.data) setFormData(form.data);
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  };

  const handleFieldChange = (section, field, value, index = null) => {
    setFormData(prev => {
      const newData = JSON.parse(JSON.stringify(prev));
      if (index !== null) {
        if (!newData[section]) newData[section] = [];
        if (!newData[section][index]) newData[section][index] = {};
        newData[section][index][field] = value;
      } else {
        if (!newData[section]) newData[section] = {};
        newData[section][field] = value;
      }
      return newData;
    });
    setErrors(prev => { const n = { ...prev }; delete n[field]; delete n[`${section}_${field}`]; return n; });
    setWarnings(prev => { const n = { ...prev }; delete n[field]; delete n[`${section}_${field}`]; return n; });
  };

  const handleCircuitDeviceChange = (index, field, value) => {
    handleFieldChange('circuits', field, value, index);
    const circuits = formData.circuits || [];
    const circuit = { ...circuits[index] };
    circuit[field] = value;

    if (circuit.protectiveDevice && circuit.deviceRating) {
      const parts = circuit.protectiveDevice.split(' ');
      const base = parts[0];
      const variant = parts.slice(1).join(' ');
      const maxZs = ZsCalculator.getMaxZs({
        type: base === 'MCB' ? 'MCB' : base === 'RCBO' ? 'RCBO' : 'Fuse',
        typeVariant: base === 'BS' ? `BS ${variant}` : variant,
        rating: parseInt(circuit.deviceRating, 10) || 0,
        disconnectionTime: '0.4s',
        is30mARCD: circuit.rcdType === '30mA'
      });
      if (maxZs !== null) handleFieldChange('circuits', 'maxZs', maxZs, index);
    }
  };

  const addRepeater = (sectionId) => {
    setFormData(prev => {
      const section = schema.sections.find(s => s.id === sectionId);
      let blank = {};
      section.fields.forEach(f => { blank[f.id] = ''; });
      if (sectionId === 'observations') blank.limitations = 'None';
      return { ...prev, [sectionId]: [...(prev[sectionId] || []), blank] };
    });
  };

  const removeRepeater = (sectionId, index) => {
    setFormData(prev => ({
      ...prev,
      [sectionId]: prev[sectionId].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const validation = validator.validate(formData);
      setErrors(validation.errors || {});
      setWarnings(validation.warnings || {});

      if (!validation.isValid) { setSaving(false); return; }

      const formRecord = {
        formId: 'electrical-condition-report',
        instanceId: formInstanceId || `eicr-${Date.now()}`,
        data: formData,
        status: 'draft',
        updatedAt: new Date(),
        lastAccessedAt: new Date()
      };

      if (formInstanceId) {
        await db.forms.update(parseInt(formInstanceId), formRecord);
      } else {
        formRecord.createdAt = new Date();
        formRecord.id = await db.forms.add(formRecord);
      }
      if (onSave) onSave(formRecord);
    } catch (error) {
      console.error('Error saving form:', error);
    }
    setSaving(false);
  };

  const renderField = (field, sectionId, value, onChange) => {
    const fieldId = `${sectionId}_${field.id}`;
    const error = errors[fieldId] || errors[field.id];
    const warning = warnings[fieldId] || warnings[field.id];

    switch (field.type) {
      case 'signature':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">{field.label}</label>
            <input type="text" id={fieldId} className="form-input" value={value || ''}
              onChange={(e) => onChange(e.target.value)} placeholder="Signature" />
          </div>
        );
      case 'boolean':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">{field.label}</label>
            <select id={fieldId} className="form-input" value={value || ''}
              onChange={(e) => onChange(e.target.value)}>
              <option value="">Select</option>
              {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
        );
      case 'number':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <input type="number" id={fieldId} step={field.step || 'any'} className={`form-input ${error ? 'border-red' : ''}`}
              value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
            {error && <span className="error-message">{error}</span>}
            {warning && <span className="warning-message">{warning}</span>}
          </div>
        );
      case 'textarea':
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <textarea id={fieldId} rows={3} className={`form-input ${error ? 'border-red' : ''}`}
              value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
            {error && <span className="error-message">{error}</span>}
          </div>
        );
      case 'select': {
        // OBSERVATION_CLASSES uses value/label objects
        const options = field.options || [];
        const isObjectOptions = options.length > 0 && typeof options[0] === 'object';
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <select id={fieldId} className={`form-input ${error ? 'border-red' : ''}`}
              value={value || ''} onChange={(e) => onChange(e.target.value)}>
              <option value="">Select {field.label}</option>
              {isObjectOptions
                ? options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)
                : options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {error && <span className="error-message">{error}</span>}
          </div>
        );
      }
      default:
        return (
          <div key={fieldId} className="form-group">
            <label htmlFor={fieldId} className="form-label">
              {field.label}{field.required && <span className="required-indicator">*</span>}
            </label>
            <input type={field.type || 'text'} id={fieldId} className={`form-input ${error ? 'border-red' : ''}`}
              value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />
            {error && <span className="error-message">{error}</span>}
          </div>
        );
    }
  };

  return (
    <div className="electrical-condition-report-form">
      <div className="form-header mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Electrical Installation Condition Report (EICR)</h1>
        <p className="text-gray-600">BS 7671:2018+A2:2022 - observations auto-classified (C1/C2/C3/FI)</p>
        {Object.keys(warnings).length > 0 && (
          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
            <p className="font-bold text-amber-800">Warnings</p>
            {Object.values(warnings).map((w, i) => <p key={i} className="text-amber-800 text-sm">{w}</p>)}
          </div>
        )}
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {schema.sections.map(section => (
          <div key={section.id} className="card mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{section.title}</h2>

            {section.repeatable ? (
              <div>
                {formData[section.id].map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4 relative">
                    <div className="absolute top-2 right-2">
                      <button type="button" onClick={() => removeRepeater(section.id, index)}
                        className="text-red-500 hover:text-red-700 p-1">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                    <h3 className="font-medium text-gray-800 mb-3">
                      {section.id === 'circuits' ? `Circuit ${index + 1}` :
                       section.id === 'observations' ? `Observation ${index + 1}` :
                       section.id === 'distributionBoards' ? `Board ${index + 1}` : `Item ${index + 1}`}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {section.fields.map(field => {
                        const onChange = section.id === 'circuits' && ['protectiveDevice', 'deviceRating', 'rcdType'].includes(field.id)
                          ? (value) => handleCircuitDeviceChange(index, field.id, value)
                          : (value) => handleFieldChange(section.id, field.id, value, index);
                        return renderField(field, `${section.id}_${index}`, item[field.id], onChange);
                      })}
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => addRepeater(section.id)} className="btn btn-secondary">
                  <SafeIcon icon={FiPlus} className="w-4 h-4" />
                  Add {section.id === 'circuits' ? 'Circuit' : section.id === 'observations' ? 'Observation' : section.id === 'distributionBoards' ? 'Board' : 'Item'}
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section.fields.map(field =>
                  renderField(field, section.id, formData[section.id]?.[field.id],
                    (value) => handleFieldChange(section.id, field.id, value))
                )}
              </div>
            )}
          </div>
        ))}

        <div className="flex gap-4 pt-6">
          <button type="button" onClick={handleSave} disabled={saving} className="btn btn-primary">
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Form'}
          </button>
          <button type="button" onClick={() => onGeneratePDF && onGeneratePDF(formData)} className="btn btn-secondary">
            <SafeIcon icon={FiFileText} className="w-4 h-4" />
            Generate PDF
          </button>
        </div>
      </form>
    </div>
  );
};

export default EICRForm;